import React, { useState } from 'react';
import R from 'ramda';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Dropdown from '@cotyorg/ccx-molecules/Dropdown/Dropdown';
import { HeroFullBlockQuarter } from '@cotyorg/ccx-organism-hero';
import Modal from 'react-modal';
import {
  getProductsWithFilteredVariants,
  getCategories,
  getAllVariants,
} from '@cotyorg/ccx-organism-filter/components/shared/helpers';
import Icon from '@cotyorg/ccx-atoms/Icon';
import {
  setVtoActualProductSku as setVtoActualProductSkuAction,
  setVtoActualProductSlug as setVtoActualProductSlugAction,
  setVtoScriptLoaded as setVtoScriptLoadedAction,
  setVtoControlsHeight as setVtoControlsHeightAction,
  setSelectedFilters as setSelectedFiltersAction,
  setActiveTab as setActiveTabAction,
} from './modules/actions';
import useScript from '../../../../utils/hooks/useScript';
import useScriptWithFacebookSharingParameters from '../../../../utils/hooks/useScriptWithFacebookSharingParameters';
import {
  filterExcludedVariantsFromProducts,
  getProductDropdownOptions,
  getProductSwatches,
  getSelectedProduct,
  onModalClose,
  onModalOpen,
  getMultiColorSwatches,
  getSelectedMultiColorProduct,
  getAllMultiColorVariants,
  getSelectedMultiColorSwatch,
  checkIfMultiColorEnabled,
} from './helpers';
import { modalStyles, emailSharingTypes } from './config';
import { Loader } from './molecules/Loader/Loader/Loader';
import {
  isXSmallBreakpoint,
  isSmallBreakpoint,
  isMediumBreakpoint,
  isLargeBreakpoint,
} from '../../utils';
import { useEvents } from './hooks/events';
import {
  useSetActualProductData,
  useInitialApplyMakeupBySku,
  useVtoInitialisation,
} from './hooks/vto';
import { dispatchEvent } from './dispatchEvent';
import { Filter } from './molecules/Filter/Filter';
import VtoButton from './molecules/VtoButton/VtoButton';
import CloseButton from './atoms/CloseButton';
import {
  onApplyFiltersButtonClick,
  onCloseButtonClick,
  onDropdownSelect,
  onHeroButtonClick,
  onOpenVto,
  onResetFiltersButtonClick,
  onSetProductID,
} from './handlers';
import ChannelAdvisorWidget from '../../molecules/ChannelAdvisorWidget';
import { Tabs } from './molecules/Tabs/Tabs';
import { ProductDetails } from './molecules/ProductDetails/ProductDetails';
import { ShadowWrapper } from './molecules/ShadowWrapper/ShadowWrapper';
import { EmailSharing } from './molecules/EmailSharing/EmailSharing';

export const generateVtoControlsClassNames = ({ isVtoLoaded }) =>
  classNames('vto__controls-container', {
    'vto__controls-container--disabled': !isVtoLoaded,
  });

const VtoModal = ({
  vto,
  currentBreakpoint,
  triggerButtonExtraClass,
  triggerButton,
  setVtoScriptLoaded,
  setVtoActualProductSku,
  setVtoActualProductSlug,
  setVtoControlsHeight,
  setSelectedFilters,
  actualProductSku,
  setOpenButtonRef,
  channelAdvisorWidget,
  disabledEmailSharing,
  setActiveTab,
  useBuyNowRetailer = false,
  openVto = false,
}) => {
  const excludedProductsVariantsSkus = R.propOr(
    [],
    'excludedProductsVariantsSkus',
    vto
  );
  const isActualVariantExcluded = R.includes(
    actualProductSku,
    excludedProductsVariantsSkus
  );
  const vtoScriptLoaded = R.prop('vtoScriptLoaded', vto);
  const actualProductSlug = R.prop('actualProductSlug', vto);
  const vtoActualProductSku = R.prop('actualProductSku', vto);
  const vtoProducts = R.prop('products', vto);
  const selectedFilters = R.prop('selectedFilters', vto);
  const allVariants = getAllVariants(vtoProducts);
  const categories = getCategories({
    categoryNames: R.propOr([], 'categoryNames', vto),
    products: allVariants,
    selectedFilters,
  });
  const perfectModuleLanguage = R.prop('perfectModuleLanguage', vto);
  const filteredProducts = getProductsWithFilteredVariants({
    products: vtoProducts,
    selectedFilters,
    categories,
  });
  const productsWithExcludedVariants = filterExcludedVariantsFromProducts({
    filteredProducts,
    excludedProductsVariantsSkus,
  });
  const dropdownProducts = getProductDropdownOptions({
    products: productsWithExcludedVariants,
  });
  const selectedProduct = getSelectedProduct({
    products: productsWithExcludedVariants,
    actualProductSlug,
  });
  const selectedProductWithoutExcludedVariants = getSelectedProduct({
    products: filteredProducts,
    actualProductSlug,
  });

  const multiColorProducts = R.propOr([], 'multiColorProducts', vto);
  const filteredMultiColorProducts = getProductsWithFilteredVariants({
    products: multiColorProducts,
    selectedFilters,
    categories,
  });
  const selectedMultiColorProduct = getSelectedMultiColorProduct({
    products: filteredMultiColorProducts,
    selectedProduct,
  });
  const correspondingMultiColorProduct = getSelectedMultiColorProduct({
    products: multiColorProducts,
    selectedProduct,
  });
  const selectedMultiColorSwatches = getMultiColorSwatches({
    product: selectedMultiColorProduct,
  });

  const swatches = getProductSwatches({
    product: selectedProduct,
  });
  const allMultiColorVariants = getAllMultiColorVariants({
    products: multiColorProducts,
  });
  const allSwatches = getProductSwatches({
    product: selectedProductWithoutExcludedVariants,
  });

  const selectedProductSwatch = getSelectedMultiColorSwatch({
    vtoActualProductSku,
    swatches,
    selectedMultiColorSwatches,
    allMultiColorVariants,
  });
  const selectedProductSwatchForEvents = R.find(
    R.propEq('sku', vtoActualProductSku),
    allSwatches
  );
  const generateMultiColorControls = !!R.prop('isMultiColorEnabled', vto);
  const enableFBSharing = !!R.prop('enableFacebookSharing', vto);
  const facebookSharingId = R.propOr('', 'facebookSharingId', vto);
  const facebookSDKUrl = R.propOr('', 'facebookSDKUrl', vto);
  const facebookSharingScriptVersion = R.propOr(
    '',
    'facebookSharingScriptVersion',
    vto
  );
  const useBuyNowWidget = R.prop('useBuyNowWidget', vto);

  const [isModalOpen, setIsModalOpen] = useState(openVto);
  const [showStartHero, setShowStartHero] = useState(true);
  const [isVtoLoaded, setIsVtoLoaded] = useState(false);
  const [isVtoOpened, setIsVtoOpened] = useState(false);
  const [showChannelAdvisor, setShowChannelAdvisor] = useState(false);
  const [isEmailSharingOpen, setIsEmailSharingOpen] = useState(false);
  const [isDownloadPictureOpen, setIsDownloadPictureOpen] = useState(false);

  const handleChannelAdvisor = () =>
    setShowChannelAdvisor((prevValue) => !prevValue);

  const eventHandler = dispatchEvent({
    product: selectedProduct,
    selectedProductSwatch,
  });

  const isMultiColorEnabled = checkIfMultiColorEnabled({
    vto,
    correspondingMultiColorProduct,
  });

  const generateVtoProductDetailsClassNames = ({
    extraClass,
    activeTabName = 'single',
  }) =>
    classNames('vto__variant-tab-content', `${extraClass}`, {
      active: R.prop('activeTab', vto) === activeTabName,
    });

  useInitialApplyMakeupBySku({
    vtoActualProductSku,
    isVtoLoaded,
  });

  useSetActualProductData({
    vtoProducts,
    actualProductSku,
    setVtoActualProductSlug,
    setVtoActualProductSku,
    isActualVariantExcluded,
    excludedProductsVariantsSkus,
  });

  useScript(R.prop('vtoSourceUrl', vto));

  useVtoInitialisation({
    currentBreakpoint,
    setVtoScriptLoaded,
    setVtoControlsHeight,
    isXSmallBreakpoint,
    isSmallBreakpoint,
    isMediumBreakpoint,
    isLargeBreakpoint,
    perfectModuleLanguage,
    disabledEmailSharing,
    enableFBSharing,
    generateMultiColorControls,
  });

  useEvents({
    vtoScriptLoaded,
    vtoActualProductSku,
    currentBreakpoint,
    setIsModalOpen,
    setShowStartHero,
    setIsVtoLoaded,
    setIsVtoOpened,
    eventHandler,
    setIsEmailSharingOpen,
    setIsDownloadPictureOpen,
  });

  useScriptWithFacebookSharingParameters({
    async: false,
    facebookSharingId,
    facebookSharingScriptVersion,
    enableFacebookSharing: enableFBSharing,
  });

  useScriptWithFacebookSharingParameters({
    crossorigin: 'anonymous',
    facebookSDKUrl,
    enableFacebookSharing: enableFBSharing,
  });

  return vtoScriptLoaded ? (
    <div>
      {!isModalOpen && (
        <VtoButton
          extraclass={triggerButtonExtraClass}
          setRef={setOpenButtonRef}
          text={
            R.prop('text', triggerButton) ||
            R.prop('text', R.prop('button', vto))
          }
          iconSrc={
            R.prop('iconUrl', triggerButton) ||
            R.prop('iconUrl', R.prop('button', vto))
          }
          onClick={() => setIsModalOpen(true)}
        />
      )}

      <Modal
        className="vto__modal"
        isOpen={isModalOpen}
        style={modalStyles}
        onAfterOpen={() =>
          onOpenVto({
            selectedProduct,
            selectedProductSwatchForEvents,
            onModalOpen,
            eventHandler,
            dispatchEvent,
          })
        }
      >
        <div className="grid-container full">
          <div className="grid-x">
            <div className="cell">
              <div className="vto-modal">
                {(!isVtoOpened || showStartHero) && (
                  <CloseButton
                    imageSrc="/ccx-files/assets/cross.svg"
                    onClick={() =>
                      onCloseButtonClick({
                        onModalClose,
                        setIsModalOpen,
                        setShowStartHero,
                        setIsVtoLoaded,
                        eventHandler,
                      })
                    }
                  />
                )}

                {!isVtoOpened && !showStartHero && <Loader />}

                {showStartHero && (
                  <div className="vto-modal__hero">
                    <HeroFullBlockQuarter
                      heroStoreDataKey="startHero"
                      ctaType="button"
                      layout="vertical"
                      isUnderlined={false}
                      onClick={() =>
                        onHeroButtonClick({
                          setShowStartHero,
                          eventHandler,
                        })
                      }
                    />
                  </div>
                )}

                <div className="cell">
                  <div id="YMK-module" />

                  {!showStartHero && (
                    <div
                      className={generateVtoControlsClassNames({ isVtoLoaded })}
                      style={{
                        height: `${R.prop('vtoControlsHeight', vto)}px`,
                      }}
                    >
                      <Dropdown
                        chevronUpIconPath="/ccx-files/assets/chevron-up-white.svg"
                        chevronDownIconPath="/ccx-files/assets/chevron-down-white.svg"
                        options={dropdownProducts}
                        selectedOption={R.prop('title', selectedProduct)}
                        hasSearch={false}
                        onSelect={({ option }) =>
                          onDropdownSelect({
                            option,
                            productsWithExcludedVariants,
                            setVtoActualProductSlug,
                            selectedProductSwatch,
                            dispatchEvent,
                            getSelectedProduct,
                            setActiveVariantTab: setActiveTab,
                            activeTab: 'single',
                          })
                        }
                      />

                      {isMultiColorEnabled && (
                        <Tabs
                          variantTabs={R.prop('tabs', vto)}
                          activeVariantTab={R.prop('activeTab', vto)}
                          setActiveVariantTab={setActiveTab}
                        />
                      )}

                      <div
                        className={generateVtoProductDetailsClassNames({
                          extraClass: 'vto__variant-tab-single-color',
                          activeTabName: 'single',
                        })}
                      >
                        <ProductDetails
                          selectedProduct={selectedProduct}
                          selectedProductSwatch={selectedProductSwatch}
                          vto={vto}
                          isModalOpen={isModalOpen}
                          useBuyNowWidget={useBuyNowWidget}
                          eventHandler={eventHandler}
                          handleChannelAdvisor={handleChannelAdvisor}
                          useBuyNowRetailer={useBuyNowRetailer}
                        />

                        <ShadowWrapper
                          selectedProduct={selectedProduct}
                          swatches={swatches}
                          vtoActualProductSku={vtoActualProductSku}
                          onSetProductID={onSetProductID}
                          setVtoActualProductSku={setVtoActualProductSku}
                          dispatchEvent={dispatchEvent}
                        />
                      </div>

                      {isMultiColorEnabled && (
                        <div
                          className={generateVtoProductDetailsClassNames({
                            extraClass: 'vto__variant-tab-multi-color',
                            activeTabName: 'multicolor',
                          })}
                        >
                          <ProductDetails
                            selectedProduct={selectedProduct}
                            selectedProductSwatch={selectedProductSwatch}
                            vto={vto}
                            useBuyNowWidget={useBuyNowWidget}
                            eventHandler={eventHandler}
                            handleChannelAdvisor={handleChannelAdvisor}
                            useBuyNowRetailer={useBuyNowRetailer}
                            isMulti
                          />

                          <ShadowWrapper
                            selectedProduct={selectedMultiColorProduct}
                            swatches={selectedMultiColorSwatches}
                            vtoActualProductSku={vtoActualProductSku}
                            onSetProductID={onSetProductID}
                            setVtoActualProductSku={setVtoActualProductSku}
                            extraClass="shadow__wrapper-multi-color"
                            dispatchEvent={dispatchEvent}
                          />
                        </div>
                      )}

                      <Filter
                        filterData={R.prop('filter', vto)}
                        filters={categories}
                        selectedFilters={selectedFilters}
                        onApplyButtonClick={({
                          selectedFilters: appliedFilters,
                        }) =>
                          onApplyFiltersButtonClick({
                            appliedFilters,
                            setSelectedFilters,
                            eventHandler,
                          })
                        }
                        onResetButtonClick={() =>
                          onResetFiltersButtonClick({
                            selectedFilters,
                            setSelectedFilters,
                            eventHandler,
                          })
                        }
                        onFiltersButtonClick={({ isOpen }) => {
                          if (!isOpen) {
                            eventHandler({
                              eventName: 'vto-generic-event-select-filter-open',
                            });
                          }
                        }}
                        onFilterSelect={({
                          filterName,
                          option,
                          isSelected,
                        }) => {
                          if (!isSelected) {
                            eventHandler({
                              eventName: 'vto-generic-event-filter-select',
                              selectedFilter: `${filterName}:${R.prop(
                                'name',
                                option
                              )}`,
                            });
                          }
                        }}
                      />
                    </div>
                  )}

                  {R.path(['downloadPicture', 'downloadText'], vto) &&
                    isEmailSharingOpen && (
                      <EmailSharing
                        imageUrl={isEmailSharingOpen}
                        selectedProduct={selectedProduct}
                        selectedProductSwatch={selectedProductSwatch}
                        vtoActualProductSku={vtoActualProductSku}
                        downloadText={R.path(
                          ['downloadPicture', 'downloadText'],
                          vto
                        )}
                        retakeText={R.path(
                          ['downloadPicture', 'retakeText'],
                          vto
                        )}
                        buttonText={R.path(['emailSharing', 'buttonText'], vto)}
                        formHeader={R.path(['emailSharing', 'formHeader'], vto)}
                        formInfo={R.path(['emailSharing', 'formInfo'], vto)}
                        formButtonText={R.path(
                          ['emailSharing', 'formButtonText'],
                          vto
                        )}
                        inputName={R.path(['emailSharing', 'inputName'], vto)}
                        inputEmail={R.path(['emailSharing', 'inputEmail'], vto)}
                        klaviyoSrc={R.path(['emailSharing', 'klaviyoSrc'], vto)}
                        klaviyoPassword={R.path(
                          ['emailSharing', 'klaviyoPassword'],
                          vto
                        )}
                        formSuccessText={R.path(
                          ['emailSharing', 'formSuccessText'],
                          vto
                        )}
                        formErrorText={R.path(
                          ['emailSharing', 'formErrorText'],
                          vto
                        )}
                        formBaseUrl={R.path(
                          ['emailSharing', 'formBaseUrl'],
                          vto
                        )}
                        emailSharingType={emailSharingTypes.EMAIL}
                        eventHandler={eventHandler}
                        onCloseClick={() => setIsEmailSharingOpen(false)}
                        onRetakeClick={() => {
                          if (window.YMK) {
                            try {
                              setIsEmailSharingOpen(false);
                              YMK.retake();
                            } catch (error) {
                              console.error(error);
                            }
                          }
                        }}
                      />
                    )}

                  {R.path(['downloadPicture', 'downloadText'], vto) &&
                    isDownloadPictureOpen && (
                      <EmailSharing
                        imageUrl={isDownloadPictureOpen}
                        selectedProduct={selectedProduct}
                        selectedProductSwatch={selectedProductSwatch}
                        vtoActualProductSku={vtoActualProductSku}
                        downloadText={R.path(
                          ['downloadPicture', 'downloadText'],
                          vto
                        )}
                        retakeText={R.path(
                          ['downloadPicture', 'retakeText'],
                          vto
                        )}
                        buttonText={R.path(['emailSharing', 'buttonText'], vto)}
                        formHeader={R.path(['emailSharing', 'formHeader'], vto)}
                        formInfo={R.path(['emailSharing', 'formInfo'], vto)}
                        formButtonText={R.path(
                          ['emailSharing', 'formButtonText'],
                          vto
                        )}
                        inputName={R.path(['emailSharing', 'inputName'], vto)}
                        inputEmail={R.path(['emailSharing', 'inputEmail'], vto)}
                        klaviyoSrc={R.path(['emailSharing', 'klaviyoSrc'], vto)}
                        klaviyoPassword={R.path(
                          ['emailSharing', 'klaviyoPassword'],
                          vto
                        )}
                        formSuccessText={R.path(
                          ['emailSharing', 'formSuccessText'],
                          vto
                        )}
                        formErrorText={R.path(
                          ['emailSharing', 'formErrorText'],
                          vto
                        )}
                        formBaseUrl={R.path(
                          ['emailSharing', 'formBaseUrl'],
                          vto
                        )}
                        emailSharingType={emailSharingTypes.DOWNLOAD}
                        eventHandler={eventHandler}
                        onCloseClick={() => setIsDownloadPictureOpen(false)}
                        onRetakeClick={() => {
                          if (window.YMK) {
                            try {
                              setIsDownloadPictureOpen(false);
                              YMK.retake();
                            } catch (error) {
                              console.error(error);
                            }
                          }
                        }}
                      />
                    )}
                </div>
              </div>
            </div>
          </div>

          {showChannelAdvisor && (
            <div className="channel-advisor-widget-wrapper">
              <span
                aria-hidden="true"
                onClick={() => handleChannelAdvisor()}
                className="channel-advisor-widget-wrapper__close"
              >
                <Icon iconSrc="/ccx-files/assets/closeIcon.svg" />
              </span>

              <ChannelAdvisorWidget
                selectedSKU={vtoActualProductSku}
                url={R.prop('url', channelAdvisorWidget)}
                caPid={R.prop('caPid', channelAdvisorWidget)}
                campaign="&campaignTag=VTO"
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  ) : null;
};

VtoModal.propTypes = {
  disabledEmailSharing: PropTypes.bool,
  channelAdvisorWidget: PropTypes.element,
  setVtoActualProductSku: PropTypes.func.isRequired,
  setVtoActualProductSlug: PropTypes.func.isRequired,
  setVtoScriptLoaded: PropTypes.func.isRequired,
  setVtoControlsHeight: PropTypes.func.isRequired,
  vto: PropTypes.shape({
    button: PropTypes.shape({
      text: PropTypes.string,
      iconUrl: PropTypes.string,
    }),
    buyNowButton: PropTypes.shape({
      text: PropTypes.string,
    }),
    downloadPicture: PropTypes.shape({
      downloadText: PropTypes.string,
      retakeText: PropTypes.string,
    }),
    emailSharing: PropTypes.shape({
      buttonText: PropTypes.string,
      formHeader: PropTypes.string,
      formInfo: PropTypes.string,
      formButtonText: PropTypes.string,
      inputName: PropTypes.string,
      inputEmail: PropTypes.string,
      klaviyoSrc: PropTypes.string,
      klaviyoPassword: PropTypes.string,
      formSuccessText: PropTypes.string,
      formErrorText: PropTypes.string,
      formBaseUrl: PropTypes.string,
    }),
    vtoSourceUrl: PropTypes.string.isRequired,
    actualProductSku: PropTypes.string,
    actualProductSlug: PropTypes.string,
    vtoScriptLoaded: PropTypes.bool,
    products: PropTypes.arrayOf(
      PropTypes.shape({
        slug: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        variants: PropTypes.arrayOf(PropTypes.object),
      })
    ),
  }),
  currentBreakpoint: PropTypes.string.isRequired,
  triggerButtonExtraClass: PropTypes.string,
  triggerButton: PropTypes.shape({
    text: PropTypes.string,
    iconUrl: PropTypes.string,
  }),
  setSelectedFilters: PropTypes.func,
  actualProductSku: PropTypes.string,
  setOpenButtonRef: PropTypes.func,
  openVto: PropTypes.bool,
  setActiveTab: PropTypes.func,
  useBuyNowRetailer: PropTypes.bool,
};

const mapDispatchToProps = (dispatch) => ({
  setVtoActualProductSku: ({ actualProductSku }) =>
    dispatch(setVtoActualProductSkuAction({ actualProductSku })),
  setVtoActualProductSlug: ({ actualProductSlug }) =>
    dispatch(setVtoActualProductSlugAction({ actualProductSlug })),
  setVtoScriptLoaded: ({ vtoScriptLoaded }) =>
    dispatch(setVtoScriptLoadedAction({ vtoScriptLoaded })),
  setVtoControlsHeight: ({ vtoControlsHeight }) =>
    dispatch(setVtoControlsHeightAction({ vtoControlsHeight })),
  setSelectedFilters: ({ selectedFilters }) =>
    dispatch(setSelectedFiltersAction({ selectedFilters })),
  setActiveTab: ({ activeTab }) => dispatch(setActiveTabAction({ activeTab })),
});

export default connect(
  ({ content, breakpoints }) => ({
    vto: R.prop('vto', content),
    currentBreakpoint: R.prop('current', breakpoints),
  }),
  mapDispatchToProps
)(VtoModal);
